import { useState, useRef } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
    Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
    Upload, Download, FileSpreadsheet, CheckCircle2, AlertTriangle,
    Plus, RefreshCw, ArrowRight, MapPin,
} from "lucide-react";

/**
 * Reconcile-and-import dialog for a generic customer CSV.
 *
 * Stage 1: pick file → POST /customers/reconcile/preview
 *   → categorized report: new / match_clean / match_diff / error
 * Stage 2: office overrides per-row actions if needed
 *   → POST /customers/reconcile/commit with {decisions: [...]}
 */
export function ReconcileCustomersDialog({ open, setOpen, onImported }) {
    const [step, setStep] = useState("upload"); // upload | review | done
    const [preview, setPreview] = useState(null);
    const [decisions, setDecisions] = useState({}); // csv_row -> action
    const [committing, setCommitting] = useState(false);
    const [result, setResult] = useState(null);
    const [busy, setBusy] = useState(false);
    const fileRef = useRef(null);

    const reset = () => {
        setStep("upload");
        setPreview(null);
        setDecisions({});
        setResult(null);
        if (fileRef.current) fileRef.current.value = "";
    };

    const downloadTemplate = async () => {
        try {
            const { data } = await api.get("/customers/reconcile/template.csv", {
                responseType: "blob",
            });
            const url = URL.createObjectURL(data);
            const a = document.createElement("a");
            a.href = url;
            a.download = "customers_template.csv";
            a.click();
            URL.revokeObjectURL(url);
        } catch {
            toast.error("Template download failed");
        }
    };

    const runPreview = async () => {
        const file = fileRef.current?.files?.[0];
        if (!file) { toast.error("Pick a CSV first"); return; }
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/customers/reconcile/preview", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setPreview(data);
            // Seed decisions with the server-suggested action for each row
            const seed = {};
            (data.items || []).forEach((it) => {
                seed[it.row.csv_row] = it.suggested_action || "skip";
            });
            setDecisions(seed);
            setStep("review");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Preview failed");
        } finally {
            setBusy(false);
        }
    };

    const commit = async () => {
        if (!preview) return;
        setCommitting(true);
        try {
            const payload = {
                decisions: (preview.items || [])
                    .filter((it) => it.category !== "error")
                    .map((it) => ({
                        row: it.row,
                        action: decisions[it.row.csv_row] || "skip",
                        matched_customer_id: it.matched_customer_id || null,
                    })),
            };
            const { data } = await api.post("/customers/reconcile/commit", payload);
            setResult(data);
            setStep("done");
            onImported?.();
            toast.success(
                `Imported ${data.inserted}, updated ${data.updated}`
            );
        } catch (e) {
            toast.error(e.response?.data?.detail || "Commit failed");
        } finally {
            setCommitting(false);
        }
    };

    const bulkSet = (category, action) => {
        setDecisions((cur) => {
            const next = { ...cur };
            (preview.items || []).forEach((it) => {
                if (it.category === category) next[it.row.csv_row] = action;
            });
            return next;
        });
    };

    return (
        <Sheet open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
            <SheetContent className="w-full sm:max-w-4xl overflow-y-auto"
                          data-testid="reconcile-customers-dialog">
                <SheetHeader>
                    <SheetTitle className="flex items-center gap-2">
                        <RefreshCw className="w-4 h-4" />
                        Reconcile customer list
                    </SheetTitle>
                    <SheetDescription>
                        Upload a CSV — I'll check every row against your existing customers,
                        show any inaccuracies, and add anyone missing.
                    </SheetDescription>
                </SheetHeader>

                {step === "upload" && (
                    <div className="mt-6 space-y-4">
                        <button onClick={downloadTemplate}
                                className="text-xs text-primary hover:underline flex items-center gap-1"
                                data-testid="reconcile-download-template">
                            <Download className="w-3 h-3" />Download CSV template
                        </button>
                        <div className="border-2 border-dashed border-border rounded-lg p-8 text-center hover:border-primary/50 transition-colors">
                            <FileSpreadsheet className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
                            <div className="text-sm font-medium mb-1">Drop a customer CSV</div>
                            <div className="text-xs text-muted-foreground mb-3">
                                Any column layout works — I recognize common headers (email,
                                phone, address, monthly_rate, etc.)
                            </div>
                            <input ref={fileRef} type="file" accept=".csv"
                                   className="block w-full text-sm border border-input rounded p-2 bg-background"
                                   data-testid="reconcile-file-input" />
                        </div>
                        <div className="flex justify-end gap-2 pt-4 border-t border-border">
                            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                            <Button onClick={runPreview} disabled={busy}
                                    data-testid="reconcile-preview-btn">
                                {busy ? "Analyzing..." : (
                                    <>Analyze CSV<ArrowRight className="w-4 h-4 ml-1" /></>
                                )}
                            </Button>
                        </div>
                    </div>
                )}

                {step === "review" && preview && (
                    <ReviewStep
                        preview={preview}
                        decisions={decisions}
                        setDecisions={setDecisions}
                        bulkSet={bulkSet}
                        onCancel={reset}
                        onCommit={commit}
                        committing={committing}
                    />
                )}

                {step === "done" && result && (
                    <DoneStep result={result} onClose={() => { reset(); setOpen(false); }} />
                )}
            </SheetContent>
        </Sheet>
    );
}

function ReviewStep({ preview, decisions, setDecisions, bulkSet,
                     onCancel, onCommit, committing }) {
    const c = preview.counts || {};
    const willInsert = preview.items.filter(
        (it) => decisions[it.row.csv_row] === "insert").length;
    const willUpdate = preview.items.filter(
        (it) => decisions[it.row.csv_row] === "update").length;

    return (
        <div className="mt-6 space-y-4">
            {(preview.warnings || []).length > 0 && (
                <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200/60 dark:border-amber-900 rounded text-xs">
                    <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                    <div className="text-amber-800 dark:text-amber-300">
                        {preview.warnings.join(" · ")}
                    </div>
                </div>
            )}

            <div className="grid grid-cols-5 gap-3">
                <Stat label="Total rows" value={c.total} />
                <Stat label="New" value={c.new} accent="green" />
                <Stat label="Differences" value={c.match_diff} accent="amber" />
                <Stat label="Bad address" value={c.address_invalid} accent={c.address_invalid ? "red" : "muted"} />
                <Stat label="Errors" value={c.error} accent={c.error ? "red" : "muted"} />
            </div>

            <div className="flex items-center gap-2 text-xs">
                <span className="text-muted-foreground">Bulk:</span>
                <Button variant="outline" size="sm"
                        onClick={() => bulkSet("new", "insert")}
                        data-testid="bulk-insert-new">
                    <Plus className="w-3 h-3 mr-1" />Insert all new ({c.new})
                </Button>
                <Button variant="outline" size="sm"
                        onClick={() => bulkSet("match_diff", "update")}>
                    Update all with diffs ({c.match_diff})
                </Button>
                <Button variant="ghost" size="sm"
                        onClick={() => bulkSet("match_diff", "skip")}>
                    Skip all diffs
                </Button>
            </div>

            <div className="border border-border rounded-md overflow-hidden">
                <div className="max-h-[55vh] overflow-y-auto"
                     data-testid="reconcile-review-table">
                    {preview.items.map((it) => (
                        <ReviewRow key={it.row.csv_row} item={it}
                                   action={decisions[it.row.csv_row] || "skip"}
                                   onAction={(a) => setDecisions((cur) => ({
                                       ...cur, [it.row.csv_row]: a,
                                   }))} />
                    ))}
                </div>
            </div>

            <div className="flex items-center justify-between pt-4 border-t border-border gap-3">
                <div className="text-xs text-muted-foreground">
                    On commit: <b>{willInsert}</b> insert · <b>{willUpdate}</b> update
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={onCancel}>Back</Button>
                    <Button onClick={onCommit} disabled={committing || (willInsert + willUpdate) === 0}
                            data-testid="reconcile-commit-btn">
                        {committing ? "Committing..." :
                            `Commit ${willInsert + willUpdate} change${willInsert + willUpdate === 1 ? "" : "s"}`}
                    </Button>
                </div>
            </div>
        </div>
    );
}

function ReviewRow({ item, action, onAction }) {
    const r = item.row;
    const category = item.category;
    const isError = category === "error";
    const isNew = category === "new";
    const isClean = category === "match_clean";
    const isDiff = category === "match_diff";

    const badgeColors = {
        new: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300",
        match_clean: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
        match_diff: "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300",
        address_invalid: "bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300",
        error: "bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300",
    };
    const badgeLabel = {
        new: "New",
        match_clean: "Match (clean)",
        match_diff: `Diff (${item.diffs?.length || 0})`,
        address_invalid: "Bad address",
        error: "Error",
    };
    const val = item.address_validation;

    return (
        <div className="border-b border-border last:border-0 p-3 hover:bg-secondary/30 transition-colors"
             data-testid={`review-row-${r.csv_row}`}>
            <div className="flex items-start gap-3">
                <span className={`text-[10px] uppercase font-semibold px-2 py-0.5 rounded ${badgeColors[category]} shrink-0 mt-0.5`}>
                    {badgeLabel[category]}
                </span>

                <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium">{r.name || "—"}</div>
                    <div className="text-xs text-muted-foreground truncate">
                        {r.address?.line1 || "?"} · {r.address?.city || "?"}
                        {r.email && <> · {r.email}</>}
                        {r.phone && <> · {r.phone}</>}
                    </div>
                    {isError && (
                        <div className="text-xs text-red-600 mt-1">{item.reason}</div>
                    )}
                    {category === "address_invalid" && (
                        <div className="mt-2 bg-red-50/60 dark:bg-red-950/20 border border-red-200/50 dark:border-red-900/50 rounded p-2 text-[11px]">
                            <div className="text-red-800 dark:text-red-300 font-medium">
                                {item.reason}
                            </div>
                            {val?.formatted && (
                                <div className="text-muted-foreground mt-0.5">
                                    Nearest match: {val.formatted}
                                </div>
                            )}
                        </div>
                    )}
                    {val && (val.status === "verified" || val.status === "partial") && val.suggestion && (
                        <div className="mt-2 bg-blue-50/60 dark:bg-blue-950/20 border border-blue-200/50 dark:border-blue-900/50 rounded p-2 text-[11px]">
                            <div className="text-blue-800 dark:text-blue-300 font-medium flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                Address auto-corrected by Google
                            </div>
                            <div className="text-muted-foreground mt-0.5 truncate">
                                {val.formatted}
                            </div>
                        </div>
                    )}
                    {isClean && (
                        <div className="text-[11px] text-muted-foreground mt-1">
                            Matches <b>{item.existing_name}</b> by {item.matched_on}
                        </div>
                    )}
                    {isDiff && (
                        <div className="mt-2 space-y-1 bg-amber-50/40 dark:bg-amber-950/20 border border-amber-200/40 dark:border-amber-900/40 rounded p-2">
                            <div className="text-[11px] text-amber-800 dark:text-amber-300 font-medium">
                                Matches <b>{item.existing_name}</b> by {item.matched_on} — differences:
                            </div>
                            {item.diffs.map((d, i) => (
                                <div key={i} className="text-[11px] flex gap-2 items-baseline">
                                    <span className="font-mono text-muted-foreground w-32 shrink-0">
                                        {d.field}
                                    </span>
                                    <span className="line-through text-muted-foreground truncate">
                                        {fmt(d.existing)}
                                    </span>
                                    <ArrowRight className="w-3 h-3 text-amber-600 shrink-0" />
                                    <span className="text-amber-800 dark:text-amber-300 font-medium truncate">
                                        {fmt(d.incoming)}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {!isError && (
                    <Select value={action} onValueChange={onAction}>
                        <SelectTrigger className="w-28 h-8 text-xs shrink-0"
                                       data-testid={`row-action-${r.csv_row}`}>
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {isNew && <SelectItem value="insert">Insert</SelectItem>}
                            {(isDiff || isClean) && <SelectItem value="update">Update</SelectItem>}
                            <SelectItem value="skip">Skip</SelectItem>
                        </SelectContent>
                    </Select>
                )}
            </div>
        </div>
    );
}

function DoneStep({ result, onClose }) {
    return (
        <div className="mt-6 space-y-4" data-testid="reconcile-done">
            <div className="flex items-center gap-3 p-4 rounded-md border border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30 dark:border-emerald-900">
                <CheckCircle2 className="w-8 h-8 text-emerald-600 dark:text-emerald-400 shrink-0" />
                <div>
                    <div className="font-heading text-lg font-semibold">Import complete</div>
                    <div className="text-sm text-muted-foreground">
                        {result.inserted} added · {result.updated} updated · {result.skipped} skipped
                        {result.errors > 0 && ` · ${result.errors} errors`}
                    </div>
                </div>
            </div>

            {result.error_details?.length > 0 && (
                <div className="space-y-1 max-h-40 overflow-y-auto border rounded p-2">
                    <div className="text-xs font-medium text-red-700 dark:text-red-400 mb-1">
                        Errors:
                    </div>
                    {result.error_details.map((e, i) => (
                        <div key={i} className="text-xs">
                            <span className="font-medium">{e.row?.name || "?"}</span>
                            <span className="text-muted-foreground"> — {e.reason}</span>
                        </div>
                    ))}
                </div>
            )}

            <div className="flex justify-end pt-4 border-t border-border">
                <Button onClick={onClose} data-testid="reconcile-done-close">
                    Done
                </Button>
            </div>
        </div>
    );
}

function Stat({ label, value, accent = "muted" }) {
    const colors = {
        muted: "text-foreground",
        green: "text-emerald-600 dark:text-emerald-400",
        amber: "text-amber-600 dark:text-amber-400",
        red: "text-red-600 dark:text-red-400",
    };
    return (
        <div className="border border-border rounded p-3 bg-secondary/30">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                {label}
            </div>
            <div className={`text-2xl font-bold tabular mt-0.5 ${colors[accent]}`}>
                {value ?? 0}
            </div>
        </div>
    );
}

function fmt(v) {
    if (v === null || v === undefined || v === "") return "—";
    if (typeof v === "object") return JSON.stringify(v);
    return String(v);
}
